import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Loader2, Download, Receipt, FileText, CreditCard } from 'lucide-react';
import API from '../../lib/api';
import { initiatePayUPayment } from '../../lib/payu';

export default function LabsStoreLedgerTab({ vendors }) {
  const [vendorId, setVendorId] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [payingId, setPayingId] = useState(null);

  const fetchLedger = useCallback(async (vid, opts = {}) => {
    if (!vid) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ vendor_id: vid });
      const s = opts.search !== undefined ? opts.search : search;
      const df = opts.date_from !== undefined ? opts.date_from : dateFrom;
      const dt = opts.date_to !== undefined ? opts.date_to : dateTo;
      if (s) params.set('search', s);
      if (df) params.set('date_from', df);
      if (dt) params.set('date_to', dt);
      const res = await API.get(`/labs-store/doctor/ledger?${params.toString()}`);
      setLedger(res.data);
    } catch (err) {
      setLedger(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (vendors?.length > 0 && !vendorId) {
      setVendorId(vendors[0].id);
      fetchLedger(vendors[0].id);
    }
  }, [vendors, vendorId, fetchLedger]);

  const applyFilters = () => fetchLedger(vendorId, { search, date_from: dateFrom, date_to: dateTo });

  const payDueFromWallet = async (orderId) => {
    setPayingId(orderId);
    try {
      await API.post(`/labs-store/doctor/bookings/${orderId}/pay-due`);
      toast.success('Due amount cleared from wallet');
      fetchLedger(vendorId, { search, date_from: dateFrom, date_to: dateTo });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Insufficient wallet balance. Try PayU instead.');
    } finally {
      setPayingId(null);
    }
  };

  const payDueViaPayU = async (orderId) => {
    setPayingId(orderId);
    try {
      await initiatePayUPayment({ token: localStorage.getItem('clinic_token'), purpose: 'labs_store_due', orderId });
    } catch (err) {
      toast.error(err.message || 'Failed to start PayU payment');
      setPayingId(null);
    }
  };

  const downloadCsv = () => {
    const token = localStorage.getItem('clinic_token');
    const params = new URLSearchParams({ vendor_id: vendorId, format: 'csv' });
    if (search) params.set('search', search);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/labs-store/doctor/ledger?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => res.blob()).then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${ledger?.vendor_name || 'vendor'}_ledger.csv`;
      a.click();
    });
  };

  return (
    <div className="space-y-4" data-testid="labs-store-ledger-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={vendorId} onValueChange={(v) => { setVendorId(v); fetchLedger(v, { search, date_from: dateFrom, date_to: dateTo }); }}>
          <SelectTrigger className="w-56" data-testid="labs-store-ledger-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
          <SelectContent>
            {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Search patient/order..." className="w-56" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applyFilters()} data-testid="labs-store-ledger-search" />
        <Input type="date" className="w-40" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="labs-store-ledger-date-from" />
        <Input type="date" className="w-40" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="labs-store-ledger-date-to" />
        <Button size="sm" onClick={applyFilters} data-testid="labs-store-ledger-filter-btn">Filter</Button>
        <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!vendorId} data-testid="labs-store-ledger-download-btn">
          <Download className="w-4 h-4 mr-1" /> Download CSV
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
      ) : ledger ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card><CardContent className="p-4"><p className="text-xs text-slate-400">Total Orders</p><p className="text-xl font-bold">{ledger.summary.total_orders}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-400">Total Spent</p><p className="text-xl font-bold">₹{ledger.summary.total_spent}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-400">Total Earnings</p><p className="text-xl font-bold text-teal-700">₹{ledger.summary.total_earnings}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-400">Due</p><p className="text-xl font-bold text-amber-600">₹{ledger.summary.total_due}</p></CardContent></Card>
          </div>
          <div className="space-y-2">
            {ledger.orders.length === 0 ? (
              <p className="text-center py-10 text-slate-400">No transactions with this vendor yet.</p>
            ) : ledger.orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between p-3 border rounded-lg flex-wrap gap-2" data-testid={`labs-store-ledger-row-${o.id}`}>
                <div className="flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium">{o.order_number}</p>
                    <p className="text-xs text-slate-400">{new Date(o.created_at).toLocaleDateString()} {o.patient_name ? `• ${o.patient_name}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {o.reports?.length > 0 && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${o.reports[0].file_url}`} target="_blank" rel="noreferrer" data-testid={`labs-store-ledger-report-${o.id}`}>
                      <Button size="sm" variant="outline"><FileText className="w-3.5 h-3.5 mr-1" /> Report</Button>
                    </a>
                  )}
                  {o.invoices?.length > 0 && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${o.invoices[0].file_url}`} target="_blank" rel="noreferrer" data-testid={`labs-store-ledger-invoice-${o.id}`}>
                      <Button size="sm" variant="outline" className="text-teal-700 border-teal-200"><Receipt className="w-3.5 h-3.5 mr-1" /> Invoice</Button>
                    </a>
                  )}
                  {o.booked_by_vendor && <span className="text-xs text-teal-700 font-medium">+₹{o.doctor_earning} earned</span>}
                  <Badge className={o.payment_status === 'due' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                    {o.payment_status === 'due' ? `Due ₹${o.due_amount}` : 'Paid'}
                  </Badge>
                  <p className="font-semibold">₹{o.total}</p>
                  {o.payment_status === 'due' && (
                    <>
                      <Button size="sm" disabled={payingId === o.id} onClick={() => payDueFromWallet(o.id)} data-testid={`labs-store-ledger-pay-wallet-${o.id}`}>
                        {payingId === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Pay from Wallet
                      </Button>
                      <Button size="sm" variant="outline" disabled={payingId === o.id} onClick={() => payDueViaPayU(o.id)} data-testid={`labs-store-ledger-pay-payu-${o.id}`}>
                        <CreditCard className="w-3.5 h-3.5 mr-1" /> PayU
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
